
import { Component, Prop, Emit, Watch, Vue } from 'vue-property-decorator';
import { v4 } from 'uuid';
import api from '@/api';
import ProductPickerRow from './ProductPickerRow.vue';

const merchantApi = api.merchant;

interface ProductPickerItem {
  key: string;
  merchantId: number;
  productId: string | null;
  quantity: number | null;
}

@Component({ components: { ProductPickerRow } })
export default class ProductPicker extends Vue {
  @Prop({ default: true }) private isSingleMode!: boolean;

  @Prop({}) private value!: Array<ProductPickerItem>;

  @Prop({ default: 0 }) private minProductCount!: number;

  @Prop({ default: 5 }) private maxProductCount!: number;

  @Prop({ default: false }) private disabled!: boolean;

  private products: Array<ProductPickerItem> = [];

  private get showRemoveProduct() {
    return this.products.length > this.minProductCount;
  }

  private get showAddProduct() {
    return this.products.length < this.maxProductCount;
  }

  private merchants: Array<{
    text: string;
    value: number;
    ignoreIds: string[];
  }> = [];

  @Watch('value', { immediate: true })
  private valueWatch(val: Array<ProductPickerItem>) {
    this.products = val.map(({ key, merchantId, productId, quantity }) => {
      const merchant = this.merchants.find((m) => m.value === merchantId);
      if (productId && merchant?.ignoreIds.includes(productId) === false) {
        merchant.ignoreIds.push(productId);
      }

      return {
        key: key || v4(),
        merchantId,
        productId,
        quantity,
      };
    });
  }

  private showRemoveIcon(index: number) {
    const {
      products: { length },
      minProductCount,
    } = this;
    return length > minProductCount || index > minProductCount;
  }

  private addProducts() {
    this.products.push({ key: v4(), merchantId: 0, productId: null, quantity: 1 });
  }

  @Emit('input')
  private removeProduct(index: number) {
    const { productId, merchantId } = this.products[index];
    if (merchantId !== 0) {
      const merchant = this.merchants.find((m) => m.value === merchantId)!;
      if (merchant.ignoreIds) {
        merchant.ignoreIds = merchant.ignoreIds.filter(
          (id) => id !== productId,
        );
      }
    }
    this.products.splice(index, 1);
    return this.products;
  }

  private productIdChanged(
    index: number,
    { merchantId, productId }: { merchantId: number; productId: string | null },
  ) {
    const product = this.products[index];
    const oldMerchant = this.merchants.find(
      (m) => m.value === product.merchantId,
    );
    const newMerchant = this.merchants.find((m) => m.value === merchantId);
    if (oldMerchant) {
      oldMerchant.ignoreIds = oldMerchant.ignoreIds.filter(
        (id) => id !== product.productId,
      );
    }
    if (newMerchant && productId) {
      newMerchant.ignoreIds.push(productId);
    }
    product.merchantId = merchantId;
    product.productId = productId;
    this.$emit('input', this.products);
  }

  private quantityChanged(index: number, quantity: number) {
    const product = this.products[index];
    product.quantity = quantity;
    this.$emit('input', this.products);
  }

  private async setMerchants() {
    const { data } = (await merchantApi.list()) || [];
    if (!!data) {
      this.merchants = data.map((v: any) => ({
        text: v.merchantName,
        value: v.merchantId,
        ignoreIds: this.products
          .filter((p) => p.merchantId === v.merchantId)
          .map((p) => p.productId),
      }));
    }
  }

  private setDefaultProduct() {
    [...Array(this.minProductCount)].map(() => {
      this.addProducts();
      return 0;
    });
  }

  private async created() {
    await this.setMerchants();
    if (this.products.length === 0 && this.minProductCount > 0) {
      this.setDefaultProduct();
    }
  }
}
